"use client";

import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

let fieldSeq = 0;
const useFieldId = (prefix: string) => {
  const [id] = useState(() => `${prefix}-${(fieldSeq += 1)}`);
  return id;
};

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: "text" | "email" | "tel" | "url";
  optional?: boolean;
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  inputMode = "text",
  optional,
}: TextFieldProps) {
  const id = useFieldId("text");
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm">
        {label}
        {optional ? (
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            (opsional)
          </span>
        ) : null}
      </Label>
      <Input
        id={id}
        type={type}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 placeholder:text-muted-foreground/45 placeholder:font-normal placeholder:italic"
      />
    </div>
  );
}

interface TextAreaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  optional?: boolean;
  rows?: number;
}

export function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  optional,
  rows = 3,
}: TextAreaFieldProps) {
  const id = useFieldId("area");
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm">
        {label}
        {optional ? (
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            (opsional)
          </span>
        ) : null}
      </Label>
      <Textarea
        id={id}
        value={value}
        placeholder={placeholder}
        rows={rows}
        onChange={(event) => onChange(event.target.value)}
        className="placeholder:text-muted-foreground/45 placeholder:font-normal placeholder:italic"
      />
    </div>
  );
}

interface MonthFieldProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

// Native month input is the most reliable mobile date picker and maps directly
// to the ResumeDocument YYYY-MM date shape.
export function MonthField({
  label,
  value,
  onChange,
  disabled,
}: MonthFieldProps) {
  const id = useFieldId("month");
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm">
        {label}
      </Label>
      <Input
        id={id}
        type="month"
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value || null)}
        className="min-h-11"
      />
    </div>
  );
}

interface BulletListProps {
  label: string;
  helper?: string;
  points: string[];
  onChange: (points: string[]) => void;
  addLabel: string;
  placeholder?: string;
}

export function BulletList({
  label,
  helper,
  points,
  onChange,
  addLabel,
  placeholder,
}: BulletListProps) {
  const update = (index: number, next: string) =>
    onChange(points.map((point, i) => (i === index ? next : point)));
  const remove = (index: number) =>
    onChange(points.filter((_, i) => i !== index));
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-sm">{label}</Label>
        {helper ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{helper}</p>
        ) : null}
      </div>
      {points.map((point, index) => (
        <div key={index} className="flex items-start gap-2">
          <Textarea
            value={point}
            placeholder={placeholder}
            rows={2}
            onChange={(event) => update(index, event.target.value)}
            className="placeholder:text-muted-foreground/45 placeholder:font-normal placeholder:italic"
          />
          {points.length > 1 ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="mt-1 shrink-0 text-muted-foreground"
              onClick={() => remove(index)}
              aria-label="Hapus poin"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...points, ""])}
      >
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        {addLabel}
      </Button>
    </div>
  );
}

interface TagFieldProps {
  label: string;
  helper?: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}

// Comma- or Enter-separated chips for skills/tools. Mobile keyboards send Enter,
// so both are handled.
export function TagField({
  label,
  helper,
  tags,
  onChange,
  placeholder,
}: TagFieldProps) {
  const id = useFieldId("tag");
  const [draft, setDraft] = useState("");

  const commit = () => {
    const value = draft.trim().replace(/,$/, "").trim();
    if (value && !tags.includes(value)) {
      onChange([...tags, value]);
    }
    setDraft("");
  };

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm">
        {label}
      </Label>
      {helper ? (
        <p className="text-xs text-muted-foreground">{helper}</p>
      ) : null}
      {tags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="gap-1 py-1">
              {tag}
              <button
                type="button"
                onClick={() => onChange(tags.filter((t) => t !== tag))}
                aria-label={`Hapus ${tag}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
      <Input
        id={id}
        value={draft}
        placeholder={placeholder}
        inputMode="text"
        onChange={(event) => {
          if (event.target.value.includes(",")) {
            setDraft(event.target.value);
            commit();
          } else {
            setDraft(event.target.value);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
          }
        }}
        onBlur={commit}
        className="min-h-11 placeholder:text-muted-foreground/45 placeholder:font-normal placeholder:italic"
      />
    </div>
  );
}
